import React, { FC, useEffect, useRef, useState } from 'react';

interface Props {
    visible: boolean;
}

const splashStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    transition: 'opacity 0.4s ease',
    pointerEvents: 'none',
};

const iconStyle: React.CSSProperties = {
    width: 72,
    height: 72,
    objectFit: 'contain',
};

const progressTrackStyle: React.CSSProperties = {
    marginTop: 32,
    width: 160,
    height: 4,
    backgroundColor: '#e9e9e9',
    borderRadius: 2,
    overflow: 'hidden',
};

const progressBarBaseStyle: React.CSSProperties = {
    height: '100%',
    backgroundColor: '#000',
    borderRadius: 2,
    width: '0%',
};

// this splash screen is the one shown after react loads and before allSettings returns data
export const SplashScreen: FC<Props> = ({ visible }) => {
    const [mounted, setMounted] = useState(visible);
    const [progress, setProgress] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // hide the initial splash screen (the one that appears before react loads)
    useEffect(() => {
        const htmlSplash = document.getElementById('html-splash');
        if (htmlSplash) htmlSplash.style.display = 'none';
    }, []);

    useEffect(() => {
        if (visible) {
            setMounted(true);
            setProgress(0);

            // fake progress: each tick adds 6% of the remaining gap to 90%, so it
            // naturally decreases speed and never actually reaches 90
            // jumps to 100 when visible becomes false (allSettings has loaded)
            intervalRef.current = setInterval(() => {
                setProgress((prev) => {
                    if (prev >= 90) {
                        clearInterval(intervalRef.current!);
                        return prev;
                    }
                    return prev + (90 - prev) * 0.06;
                });
            }, 100);
        } else {
            if (intervalRef.current) clearInterval(intervalRef.current);
            setProgress(100);
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [visible]);

    if (!mounted) return null;

    return (
        <div
            style={{ ...splashStyle, opacity: visible ? 1 : 0 }}
            onTransitionEnd={() => { if (!visible) setMounted(false); }}
        >
            <img src="/pwa-icon-192.png" alt="" style={iconStyle} />
            <div style={progressTrackStyle}>
                <div
                    style={{
                        ...progressBarBaseStyle,
                        width: `${progress}%`,
                        transition: progress === 100 ? 'width 0.2s ease' : 'width 0.1s linear',
                    }}
                />
            </div>
        </div>
    );
};
